import { Box, Stack, TextField } from "@mui/material";
import TooltipText from "../../../components/inputComponents/TooltipText";
import { ToolTipPlacement } from "../../../types/componentTypes/tooltip";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { DataToExport } from "../../../types/componentTypes/ParticipationExportTypes";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ExportStatusMessage from "./ExportStatusMessage";
import { ConfigType } from "../../../types/configuration";
import moment, { Moment } from "moment-timezone";

interface CustomWinnersExportProps {
    mechanic: "instantWin" | "promoEntry";
    numberOfWinners: number;
    handleNumberOfWinnerOnChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    copyToClipboard: (csv: string) => void;
    dataToExport: DataToExport;
    isBatchForWinners: boolean;
    iwCustomDates: { startDate: number; endDate: number };
    setIwCustomDates: Dispatch<SetStateAction<CustomWinnersExportProps["iwCustomDates"]>>;
    config: ConfigType;
    setDataToExport: Dispatch<SetStateAction<DataToExport>>
}

const CustomWinnersExport = ({
    mechanic,
    numberOfWinners,
    handleNumberOfWinnerOnChange,
    copyToClipboard,
    dataToExport,
    isBatchForWinners,
    iwCustomDates,
    setIwCustomDates,
    config,
    setDataToExport
}: CustomWinnersExportProps) => {
    const configStartDate = config.configurationParameters.configurationStartUtc;
    const configEndDate = config.configurationParameters.configurationEndUtc;
    const timezone = config.configurationParameters.configurationDatesTimezone || moment.tz.guess();

    const handleIwCustomDates = (date: Moment, prefix: 'start' | 'end') => {
        const field = `${prefix}Date`;
        setIwCustomDates((prev) => ({ ...prev, [field]: date.valueOf() }));
        setDataToExport(null);
    };

    return (
        <Box className='custom-participation-box'>
            {mechanic === "promoEntry" ? (
                <>
                    <TooltipText
                        text='Number of winners'
                        textVariant='body1'
                        styleName='number-of-winner-tooltip'
                        iconSize='medium'
                        tooltip='The system will randomly select the number of winners entered in this field.'
                        placement={ToolTipPlacement.RightEnd}
                    />
                    <TextField
                        error={numberOfWinners < 1}
                        className='pe-button'
                        value={numberOfWinners}
                        onChange={(e) => handleNumberOfWinnerOnChange(e)}
                        helperText='Please enter a positive number'
                    />
                </>
            ) : (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Stack direction='row' spacing={2}>
                        <DesktopDatePicker
                            label='Start Date'
                            value={moment.tz(iwCustomDates.startDate, timezone)}
                            onChange={(date: Moment) =>
                                handleIwCustomDates(date, 'start')
                            }
                            minDate={moment.tz(configStartDate, timezone)}
                            maxDate={moment.tz(configEndDate, timezone)}
                            timezone={timezone}
                        />
                        <DesktopDatePicker
                            label='End Date'
                            value={moment.tz(iwCustomDates.endDate, timezone)}
                            onChange={(date: Moment) =>
                                handleIwCustomDates(date, 'end')
                            }
                            minDate={moment.tz(iwCustomDates.startDate, timezone).add(1, "day")}
                            maxDate={moment(configEndDate, timezone)}
                            timezone={timezone}
                        />
                    </Stack>
                </LocalizationProvider>
            )}
            {isBatchForWinners && dataToExport && (
                <ExportStatusMessage
                    message='Due to the large number of participants, the file is still in progress. Please check the link below again in 15 minutes.'
                    csvUrl={dataToExport.csv}
                    onCopy={() => copyToClipboard(dataToExport.csv)}
                />
            )}
        </Box>
    );
};

export default CustomWinnersExport;
